import type { PrepressJobOperation, PrepressJobSummary } from '@united-square/catalog-contract'
import { resolveApiUrl } from '@runtime/shared/api/fetchJSON'
import { createAdminApiError, createAdminAuthHeaders } from '@studio/routes/hub/admin/adminApiAuth'

export const PREPRESS_JOBS_ENDPOINT = '/api/prepress/jobs'
export const PREPRESS_JOBS_CHANGED = 'prepress-jobs-changed'

async function request(authToken: string, path: string, init: RequestInit = {}) {
  const response = await fetch(resolveApiUrl(`${PREPRESS_JOBS_ENDPOINT}${path}`), {
    ...init,
    headers: { ...createAdminAuthHeaders(authToken), ...init.headers },
  })
  if (!response.ok) throw await createAdminApiError(response, PREPRESS_JOBS_ENDPOINT)
  return response
}

export async function listPrepressJobs(
  authToken: string,
  signal?: AbortSignal,
): Promise<PrepressJobSummary[]> {
  return (await request(authToken, '', { signal })).json() as Promise<PrepressJobSummary[]>
}

export async function submitPrepressJob(
  authToken: string,
  operation: PrepressJobOperation,
  payload: unknown | FormData,
  signal?: AbortSignal,
) {
  const headers: Record<string, string> = { 'Idempotency-Key': crypto.randomUUID() }
  let body: BodyInit
  if (payload instanceof FormData) {
    payload.set('operation', operation)
    payload.set('payload', String(payload.get('metadata') || '{}'))
    payload.delete('metadata')
    body = payload
  } else {
    headers['Content-Type'] = 'application/json'
    body = JSON.stringify({ operation, payload })
  }
  let response: Response | undefined
  for (let attempt = 0; attempt < 2; attempt++) {
    try {
      response = await request(authToken, '', { method: 'POST', body, headers, signal })
      break
    } catch (error) {
      if (!(error instanceof TypeError) || signal?.aborted || attempt === 1) throw error
    }
  }
  const job = (await response!.json()) as PrepressJobSummary
  window.dispatchEvent(new Event(PREPRESS_JOBS_CHANGED))
  return job
}

export async function waitForPrepressJob(
  authToken: string,
  initial: PrepressJobSummary,
  signal?: AbortSignal,
) {
  let job = initial
  while (job.status === 'queued' || job.status === 'running') {
    await new Promise<void>((resolve, reject) => {
      const abort = () => {
        clearTimeout(timer)
        reject(new DOMException('Aborted', 'AbortError'))
      }
      const timer = setTimeout(() => {
        signal?.removeEventListener('abort', abort)
        resolve()
      }, 2000)
      signal?.addEventListener('abort', abort, { once: true })
      if (signal?.aborted) abort()
    })
    job = (await (await request(authToken, `/${job.id}`, { signal })).json()) as PrepressJobSummary
    window.dispatchEvent(new Event(PREPRESS_JOBS_CHANGED))
  }
  if (job.status !== 'succeeded') throw new Error(job.errorCode || `prepress_job_${job.status}`)
  return job
}

export async function runPrepressJob<T>(
  authToken: string,
  operation: PrepressJobOperation,
  payload: unknown | FormData,
  signal?: AbortSignal,
): Promise<{ job: PrepressJobSummary; result: T }> {
  const job = await waitForPrepressJob(
    authToken,
    await submitPrepressJob(authToken, operation, payload, signal),
    signal,
  )
  const result = (await (await request(authToken, `/${job.id}/result`, { signal })).json()) as T
  return { job, result }
}

export function fetchPrepressArtifact(authToken: string, id: string, signal?: AbortSignal) {
  return request(authToken, `/${id}/artifact`, { signal })
}

export async function downloadPrepressArtifact(authToken: string, job: PrepressJobSummary) {
  const response = await fetchPrepressArtifact(authToken, job.id)
  await downloadResponse(response, job.artifact?.fileName || 'prepress.zip')
}

export async function downloadPrepressResult(authToken: string, job: PrepressJobSummary) {
  await downloadResponse(await request(authToken, `/${job.id}/result`), `prepress-${job.id}.json`)
}

async function downloadResponse(response: Response, fileName: string) {
  const blob = await response.blob()
  const url = URL.createObjectURL(blob)
  const anchor = document.createElement('a')
  anchor.href = url
  anchor.download = fileName
  anchor.click()
  setTimeout(() => URL.revokeObjectURL(url), 1000)
}
